import React, { useState, useEffect } from 'react';
import {
  WalletIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  ClipboardIcon,
  CheckIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

const CRYPTO_OPTIONS = [
  { symbol: 'BTC', name: 'Bitcoin', network: 'Bitcoin', min: 0.0001, icon: '₿' },
  { symbol: 'ETH', name: 'Ethereum', network: 'Ethereum', min: 0.01, icon: 'Ξ' },
  { symbol: 'BNB', name: 'BNB', network: 'BSC', min: 0.01, icon: 'ⓑ' },
  { symbol: 'USDT', name: 'Tether USD', network: 'Ethereum', min: 10, icon: '₮' }
];

function WalletManager() {
  const [balance, setBalance] = useState({ btc: 0, eth: 0, bnb: 0, usdt: 0, usd: 0 });
  const [transactions, setTransactions] = useState([]);
  const [prices, setPrices] = useState({});
  const [selectedCrypto, setSelectedCrypto] = useState(CRYPTO_OPTIONS[0]);
  const [depositAddress, setDepositAddress] = useState('');
  const [showDeposit, setShowDeposit] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [balanceRes, txRes] = await Promise.all([
        api.get('/crypto/balance'),
        api.get('/crypto/transactions')
      ]);
      
      if (balanceRes.data.success) {
        setBalance(balanceRes.data.balance);
        setPrices(balanceRes.data.prices);
      }
      if (txRes.data.success) {
        setTransactions(txRes.data.transactions);
      }
    } catch (error) {
      console.error('Error loading wallet:', error);
      toast.error('Error al cargar wallet');
    }
    setLoading(false);
  };

  const checkWalletPermissions = () => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (!user.permissions?.canUseWallet) {
        toast.error('No tienes permiso para usar la wallet');
        return false;
      }
      return true;
    } catch (e) {}
  }
  return true;
};

  const generateAddress = async () => {
    if (!checkWalletPermissions()) return;
    setLoading(true);
    try {
      const response = await api.post('/crypto/generate-address', {
        currency: selectedCrypto.symbol,
        network: selectedCrypto.network
      });
      
      if (response.data.success) {
        setDepositAddress(response.data.address);
        setShowDeposit(true);
      }
    } catch (error) {
      toast.error('Error al generar dirección');
    }
    setLoading(false);
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(depositAddress);
    setCopied(true);
    toast.success('Dirección copiada');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const formatAmount = (amount, symbol) => {
    const absAmount = Math.abs(amount);
    if (symbol === 'BTC') return `${absAmount.toFixed(6)} BTC`;
    if (symbol === 'ETH' || symbol === 'BNB') return `${absAmount.toFixed(4)} ${symbol}`;
    if (symbol === 'USDT') return `${absAmount.toFixed(2)} USDT`;
    return `${absAmount.toFixed(2)} USD`;
  };

  const getTotalUSD = () => {
    let total = balance.usd || 0;
    if (balance.btc && prices.btc) total += balance.btc * prices.btc;
    if (balance.eth && prices.eth) total += balance.eth * prices.eth;
    return total;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#000000',
      color: '#ffffff',
      padding: '1rem',
      paddingBottom: '6rem'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1rem 0',
        marginBottom: '2rem'
      }}>
        <button
          onClick={() => window.history.back()}
          style={{
            background: 'none',
            border: 'none',
            color: '#00ffff',
            cursor: 'pointer'
          }}
        >
          <ArrowLeftIcon style={{ width: '1.5rem', height: '1.5rem' }} />
        </button>
        <h1 style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Mi Wallet
        </h1>
      </div>

      {/* Balance Total */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a1a, #0a0a0a)',
        border: '1px solid #00ffff',
        borderRadius: '1.5rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        textAlign: 'center'
      }}>
        <WalletIcon style={{ width: '2rem', height: '2rem', color: '#00ffff', margin: '0 auto 0.5rem' }} />
        <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Saldo Total</div>
        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#00ffff' }}>
          ${getTotalUSD().toFixed(2)} USD
        </div>
      </div>

      {/* Balances por Cripto */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        {CRYPTO_OPTIONS.map(crypto => {
          const amount = balance[crypto.symbol.toLowerCase()] || 0;
          const valueUSD = prices[crypto.symbol.toLowerCase()] ? amount * prices[crypto.symbol.toLowerCase()] : 0;
          
          return (
            <div key={crypto.symbol} style={{
              background: '#111111',
              border: '1px solid #222222',
              borderRadius: '1rem',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.25rem' }}>{crypto.icon}</span>
                <span style={{ fontWeight: 'bold' }}>{crypto.symbol}</span>
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                {amount.toFixed(crypto.symbol === 'BTC' ? 6 : 4)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                ≈ ${valueUSD.toFixed(2)} USD
              </div>
            </div>
          );
        })}
      </div>

      {/* Acciones */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <button
          onClick={() => {
            setShowDeposit(false);
            generateAddress();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '1rem',
            background: 'linear-gradient(135deg, #00ff88, #00aa55)',
            border: 'none',
            borderRadius: '1rem',
            color: '#000000',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          <ArrowDownTrayIcon style={{ width: '1.25rem' }} />
          Depositar
        </button>
        <button
          onClick={() => toast.info('Próximamente: Retiros')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '1rem',
            background: '#1a1a1a',
            border: '1px solid #333333',
            borderRadius: '1rem',
            color: '#9ca3af',
            cursor: 'pointer'
          }}
        >
          <ArrowUpTrayIcon style={{ width: '1.25rem' }} />
          Retirar
        </button>
      </div>

      {/* Modal de Depósito */}
      {showDeposit && depositAddress && (
        <div style={{
          background: '#111111',
          border: '1px solid #00ffff',
          borderRadius: '1rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ color: '#00ffff', marginBottom: '1rem' }}>
            Depositar {selectedCrypto.symbol}
          </h3>
          
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
              Red: {selectedCrypto.network}
            </div>
            <div style={{
              background: '#1a1a1a',
              padding: '1rem',
              borderRadius: '0.5rem',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              wordBreak: 'break-all',
              marginBottom: '0.5rem'
            }}>
              {depositAddress}
            </div>
            <button
              onClick={copyAddress}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: '#00ffff20',
                border: '1px solid #00ffff',
                borderRadius: '0.5rem',
                color: '#00ffff',
                cursor: 'pointer'
              }}
            >
              {copied ? (
                <CheckIcon style={{ width: '1rem' }} />
              ) : (
                <ClipboardIcon style={{ width: '1rem' }} />
              )}
              {copied ? 'Copiado!' : 'Copiar dirección'}
            </button>
          </div>
          
          <div style={{
            background: '#ff660020',
            border: '1px solid #ff6600',
            borderRadius: '0.5rem',
            padding: '0.75rem',
            fontSize: '0.75rem',
            color: '#ff6600'
          }}>
            ⚠️ Envía SOLAMENTE {selectedCrypto.symbol} en la red {selectedCrypto.network}. 
            Mínimo: {selectedCrypto.min} {selectedCrypto.symbol}
          </div>
        </div>
      )}

      {/* Historial de Transacciones */}
      <div style={{
        background: '#111111',
        border: '1px solid #222222',
        borderRadius: '1rem',
        padding: '1rem'
      }}>
        <h3 style={{ color: '#00ffff', marginBottom: '1rem' }}>Historial de Transacciones</h3>
        
        {transactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
            No hay transacciones
          </div>
        ) : (
          transactions.map((tx, idx) => (
            <div key={idx} style={{
              padding: '0.75rem',
              borderBottom: idx < transactions.length - 1 ? '1px solid #222222' : 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.25rem'
                }}>
                  <span style={{
                    color: tx.type === 'deposit' ? '#00ff88' : 
                           tx.type === 'purchase' ? '#ff6600' : 
                           tx.type === 'admin_add' ? '#00ffff' : '#ff0033'
                  }}>
                    {tx.type === 'deposit' && '📥 Depósito'}
                    {tx.type === 'purchase' && '🛒 Compra'}
                    {tx.type === 'admin_add' && '✨ Bono'}
                    {tx.type === 'admin_remove' && '📤 Retiro'}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                    {formatDate(tx.timestamp)}
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  {tx.description || tx.type}
                </div>
              </div>
              <div style={{
                fontWeight: 'bold',
                color: tx.amount > 0 ? '#00ff88' : '#ff0033'
              }}>
                {tx.amount > 0 ? '+' : ''}{formatAmount(tx.amount, tx.currency)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default WalletManager;